/**
 * SendMessageModal
 * One dialog for sending a message to a client, a lead or a license — now, or at
 * a time the sender picks, optionally repeating. Rendered conditionally by the
 * parent; unmount to close.
 *
 * Cloned from SendCardModal rather than bolted onto it: a business card is a
 * fixed layout with a person's details, and a message is a template with words.
 * They compose (a message can be *signed* with a card) but they are not the same
 * dialog, and merging them would have produced one form with two disabled halves.
 *
 * Six things here are deliberate:
 *
 * 1. **Repeat is hidden until a schedule is chosen.** "Send now, every week" is
 *    not a thing, and a control that is meaningless most of the time is worse
 *    than one that appears when it means something.
 * 2. **The time is sent as a wall clock plus a zone**, never as UTC. The browser
 *    knows the sender's IANA zone, and the server needs it: a repeat is computed
 *    on the local clock so "the 1st at 9 AM" stays 9 AM after the clocks change.
 * 3. **Do Not Contact says something different for now than for later.** Sending
 *    now warns and can be overridden, because the person here can see context the
 *    list cannot. A scheduled message is re-checked when it fires, with nobody
 *    present, and is blocked outright — so the warning says that instead of
 *    offering an override that would not survive the wait.
 * 4. **Preview renders against the real record**, not sample values — the
 *    question before pressing Send is "is this right for this client".
 * 5. **Only the common path is on screen.** What to send, who to, and Send. The
 *    signature/subject/wording controls and the scheduling controls each sit
 *    behind a disclosure, because "send this message to this person, now" is
 *    what nearly every send is. Nothing was removed — a collapsed section still
 *    says what it is set to, and the wording section opens itself for a message
 *    that has no words of its own. Attaching a spare file joined them once
 *    every message began accepting one, but *only* for a message that names no
 *    files of its own; the Files section below spells that rule out, because a
 *    message that is waiting on a file must never hide the box it is waiting on.
 * 6. **A subject template is only shown as a placeholder when it says
 *    something.** See `hasOwnSubject`.
 * 7. **A first contact suggests a phone call, and never blocks.** On 2026-10-21 a
 *    cold e-mail about a construction deadline read as a sales pitch to the
 *    school that got it. So when nobody has dealt with the recipient yet (a bare
 *    license, or a lead still at "New"), the dialog says a call usually goes
 *    better. Never for a client, whom we already work with. See `FirstContact`.
 */
import { useState, useEffect } from 'react'
import { uploadFile } from '../services/files'
import Modal from './Modal'
import FieldHint from './FieldHint'
import {
  listComposableTemplates,
  listBusinessCards,
  previewMessage,
  sendMessageNow,
  scheduleMessage,
  getClientDncStatus,
  getLeadDncStatus,
  getEmailDncStatus,
  toggleDnc,
  getTrackedLead,
} from '../services/crm'
import { useAuth } from '../services/auth'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import { pickDefaultCard } from '../utils/businessCardForm'
import type {
  BusinessCardTemplate,
  ComposableTemplate,
  StoredFileInfo,
  MessageAttachmentRef,
  DncStatus,
  MessageComposition,
  MessageRecurrence,
} from '../types'

interface Props {
  onClose: () => void
  /** Exactly one of these three identifies who the message is for. */
  clientId?: number
  leadId?: number
  fccUsi?: number
  /** Pre-filled recipient (editable) */
  defaultEmail?: string
  /**
   * Open with this message already chosen — the caller picked it by name from
   * the Send menu, so landing on a different one would undo that choice. Still
   * changeable here. Ignored if the message is no longer offered.
   */
  defaultTemplateId?: number | null
  /**
   * The same, by the message's built-in name rather than its id — for a page
   * that knows which message fits its purpose but cannot know the id a given
   * installation gave it. `defaultTemplateId` wins when both are given.
   */
  defaultTemplateKey?: string | null
  /** Called after a successful send or a successful schedule */
  onSent?: () => void
  /**
   * The construction deadline this message is about (Construction Deadlines).
   * A send that goes out now records that licensee as contacted; a scheduled
   * one does not, because nobody has been contacted until it leaves.
   */
  constructionUsi?: number
}

type When = 'now' | 'later'

/**
 * Nobody here has dealt with the recipient yet. `null` means there is history
 * (a client, or a lead someone has moved past "New"), or it is still loading.
 */
interface FirstContact {
  /** A number to call, when the lead has one. */
  phone: string | null
  /** The recipient is a lead (whose state can be changed), not a bare license. */
  isLead: boolean
}

const RECURRENCES: { value: MessageRecurrence; label: string }[] = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly' },
]

/** The sender's own zone, which is what a picked time means. */
function browserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** `datetime-local` value for "an hour from now", rounded to the hour. */
function defaultScheduleValue(): string {
  const d = new Date()
  d.setHours(d.getHours() + 1, 0, 0, 0)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/**
 * Does this message carry a subject of its own?
 *
 * A subject template is one of two things: real wording with values dropped
 * into it ("FCC License Renewal Reminder — {{ call_sign }}"), or nothing but a
 * value ("{{ message_subject }}"). Strip the {{ … }} placeholders out and look
 * at what is left: any word or number means the template says something, and
 * showing it as the Subject box's placeholder previews what the recipient will
 * see. Only punctuation and spaces left means the subject *is* whatever the
 * sender types, and showing the template would put "{{ message_subject }}" in
 * front of them, which tells them nothing.
 */
function hasOwnSubject(template: ComposableTemplate | null): boolean {
  const raw = template?.subject_template ?? ''
  return /[\p{L}\p{N}]/u.test(raw.replace(/\{\{[^}]*\}\}/g, ' '))
}

export default function SendMessageModal({
  onClose,
  clientId,
  leadId,
  fccUsi,
  defaultEmail = '',
  defaultTemplateId = null,
  defaultTemplateKey = null,
  onSent,
  constructionUsi,
}: Props) {
  const { user } = useAuth()

  const [templates, setTemplates] = useState<ComposableTemplate[]>([])
  const [cards, setCards] = useState<BusinessCardTemplate[]>([])
  const [loading, setLoading] = useState(true)

  const [templateId, setTemplateId] = useState<number | null>(null)
  const [signatureCardId, setSignatureCardId] = useState<number | null>(null)
  const [toEmail, setToEmail] = useState(defaultEmail)
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')

  // Signature, subject and wording live behind a disclosure: most sends use the
  // message as written, and a form that shows every override at once is what
  // made this dialog feel long.
  const [optionsOpen, setOptionsOpen] = useState(false)

  const [when, setWhen] = useState<When>('now')
  const [sendAtLocal, setSendAtLocal] = useState(defaultScheduleValue)
  const [recurrence, setRecurrence] = useState<MessageRecurrence>('none')
  const [interval, setInterval] = useState(1)
  const [until, setUntil] = useState('')

  // Files to send. Keyed by slot label for a template's named slots, and a
  // growing list for unlabelled extras — the two behave differently enough
  // (one is required and replaceable, the other is additive) to keep apart.
  const [slotFiles, setSlotFiles] = useState<Record<string, StoredFileInfo>>({})
  const [extraFiles, setExtraFiles] = useState<StoredFileInfo[]>([])
  const [uploading, setUploading] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState('')
  // Only used when the message names no files of its own — see the Files
  // section below for which of the two shapes gets rendered.
  const [attachOpen, setAttachOpen] = useState(false)

  const [firstContact, setFirstContact] = useState<FirstContact | null>(null)

  const [dnc, setDnc] = useState<DncStatus | null>(null)
  const [dncAcknowledged, setDncAcknowledged] = useState(false)
  const [liftingDnc, setLiftingDnc] = useState(false)

  const [preview, setPreview] = useState<{ subject: string; html: string } | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const [error, setError] = useState('')

  // Derived from the chosen message. Declared up here because the effect below
  // depends on it, and a dependency array is read while rendering.
  const selectedTemplate = templates.find((t) => t.id === templateId) ?? null
  const slots = selectedTemplate?.attachment_slots ?? []
  const allowsExtras = selectedTemplate?.allow_extra_attachments ?? false
  /** The message brings no subject with it, so the one typed here is the subject. */
  const senderWritesSubject = selectedTemplate !== null && !hasOwnSubject(selectedTemplate)

  // The suppression list can match the *address*, not only the record, so the
  // warning has to follow what is typed into the To box rather than being read
  // once when the dialog opens. Debounced for the same reason a search box is:
  // this fires a request, and the box changes on every keystroke.
  const debouncedToEmail = useDebouncedValue(toEmail)

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let cancelled = false
    if (clientId !== undefined) {
      setFirstContact(null)
    } else if (leadId !== undefined) {
      // Only "New" counts: working/waiting means a person has already moved it on.
      getTrackedLead(leadId)
        .then((lead) => {
          if (cancelled) return
          setFirstContact(lead.work_state === 'new'
            ? { phone: lead.contact_phone || lead.entity_phone || null, isLead: true }
            : null)
        })
        // Advice only: if the lead cannot be read, say nothing rather than guess.
        .catch(() => { if (!cancelled) setFirstContact(null) })
    } else if (fccUsi !== undefined) {
      setFirstContact({ phone: null, isLead: false })
    }
    return () => { cancelled = true }
  }, [clientId, leadId, fccUsi])

  useEffect(() => {
    // The acknowledgement is about the suppression currently on screen. Changing
    // the address asks a different question, so a tick made about the old one
    // must not carry over and quietly authorise a send nobody agreed to.
    setDncAcknowledged(false)
    void loadDncStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedToEmail])

  useEffect(() => {
    // A message with no subject of its own is nothing *but* the subject and
    // wording boxes, so leaving them collapsed would hide the point of it.
    // Only ever opens: closing it again is the sender's to decide.
    if (senderWritesSubject) setOptionsOpen(true)
  }, [senderWritesSubject])

  async function load() {
    setLoading(true)
    try {
      const [tmpls, allCards] = await Promise.all([
        listComposableTemplates(),
        listBusinessCards().catch(() => [] as BusinessCardTemplate[]),
      ])
      setTemplates(tmpls)
      // The caller's choice wins when it is still on offer; otherwise fall back
      // to the first, which is what an unprompted "Send a message" gets.
      const byKey = tmpls.find((t) => t.template_key === defaultTemplateKey)?.id
      setTemplateId(
        (defaultTemplateId !== null && tmpls.some((t) => t.id === defaultTemplateId)
          ? defaultTemplateId
          : byKey ?? tmpls[0]?.id) ?? null
      )
      const active = allCards.filter((c) => c.is_active)
      setCards(active)
      // The same rule the card picker uses, so the two dialogs agree on which
      // signature is "yours".
      setSignatureCardId(pickDefaultCard(active, user?.id)?.id ?? null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the messages')
    } finally {
      setLoading(false)
    }
  }

  async function loadDncStatus() {
    // Three cases, because the list can be reached two different ways. A client
    // or lead resolves the license-shaped match types *and* the address; with
    // neither (a bare FCC USI target) only the address can be asked about, which
    // is still better than the nothing this used to do.
    const email = debouncedToEmail.trim()
    try {
      if (clientId !== undefined) {
        setDnc(await getClientDncStatus(clientId, email || undefined))
      } else if (leadId !== undefined) {
        setDnc(await getLeadDncStatus(leadId, email || undefined))
      } else if (email.includes('@')) {
        setDnc(await getEmailDncStatus(email))
      } else {
        setDnc(null)
      }
    } catch {
      // A failed check must not block sending; the API re-checks and is the gate.
      setDnc(null)
    }
  }

  async function handleLiftDnc(matchType: string, value: string) {
    setLiftingDnc(true)
    setError('')
    try {
      await toggleDnc({ match_type: matchType, value })
      await loadDncStatus()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the Do Not Contact entry')
    } finally {
      setLiftingDnc(false)
    }
  }

  function composition(): MessageComposition | null {
    if (!templateId) {
      setError('Choose a message')
      return null
    }
    if (!toEmail.trim()) {
      setError('A recipient is required')
      return null
    }
    return {
      client_id: clientId,
      lead_id: leadId,
      fcc_usi: fccUsi,
      to_email: toEmail.trim(),
      template_id: templateId,
      signature_card_id: signatureCardId ?? undefined,
      subject_override: subject.trim() || undefined,
      body_override: body.trim() || undefined,
    }
  }

  async function handlePreview() {
    const base = composition()
    if (!base) return
    setPreviewing(true)
    setError('')
    try {
      setPreview(await previewMessage(base))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not render the preview')
    } finally {
      setPreviewing(false)
    }
  }

  /** Slots the template insists on that still have nothing in them. */
  const unfilledRequired = slots
    .filter((slot) => slot.required && !slotFiles[slot.label])
    .map((slot) => slot.label)

  const attachmentRefs: MessageAttachmentRef[] = [
    ...Object.entries(slotFiles).map(([label, file]) => ({
      file_id: file.id,
      slot_label: label,
    })),
    ...extraFiles.map((file) => ({ file_id: file.id })),
  ]

  async function handleUpload(file: File, slotLabel?: string) {
    setUploading(slotLabel ?? '__extra__')
    setUploadError('')
    try {
      const slot = slots.find((s) => s.label === slotLabel)
      const stored = await uploadFile(file, {
        leadId: leadId ?? undefined,
        clientId: clientId ?? undefined,
        slotLabel,
        // Retention comes from the slot, which is the whole reason slots carry
        // it — nobody should have to decide how long to keep a file mid-send.
        retentionDays:
          slot?.retention_policy === 'days' ? slot.retention_days ?? undefined : undefined,
        permanent: slot?.retention_policy === 'permanent',
      })
      if (slotLabel) {
        setSlotFiles((prev) => ({ ...prev, [slotLabel]: stored }))
      } else {
        setExtraFiles((prev) => [...prev, stored])
      }
      setPreview(null)
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(null)
    }
  }

  function handleAddExtra(file: File) {
    void handleUpload(file)
  }

  function handleRemoveExtra(fileId: number) {
    setExtraFiles((prev) => prev.filter((f) => f.id !== fileId))
    setPreview(null)
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const base = composition()
    if (!base) return

    setBusy(true)
    setError('')
    try {
      if (when === 'now') {
        const res = await sendMessageNow({
          ...base,
          acknowledge_dnc: dncAcknowledged || undefined,
          attachments: attachmentRefs.length ? attachmentRefs : undefined,
          construction_usi: constructionUsi,
        })
        setResult(res.message)
        if (res.sent) onSent?.()
      } else {
        const queued = await scheduleMessage({
          ...base,
          send_at_local: sendAtLocal,
          send_timezone: browserTimezone(),
          recurrence,
          recurrence_interval: interval,
          recurrence_until_local: recurrence === 'none' || !until ? undefined : until,
        })
        setResult(
          `Queued — ${queued.recurrence_summary === 'once' ? 'sends' : `first send`} ` +
            `${new Date(sendAtLocal).toLocaleString()}` +
            (queued.recurrence_summary === 'once' ? '' : `, then ${queued.recurrence_summary}`)
        )
        onSent?.()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the message')
    } finally {
      setBusy(false)
    }
  }

  const companyCards = cards.filter((c) => c.card_type === 'company')
  const personalCards = cards.filter((c) => c.card_type === 'personal')
  // Collapsed does not mean invisible: signing as the wrong person is a real
  // mistake, so the header says who is signing and whether anything was
  // overridden, without the three controls being on screen.
  const signedByName = cards.find((c) => c.id === signatureCardId)?.sender_name
  const optionsSummary = [
    signedByName ? `signed by ${signedByName}` : 'no signature',
    subject.trim() ? 'custom subject' : null,
    body.trim() ? 'custom wording' : null,
  ]
    .filter(Boolean)
    .join(' · ')
  // Same idea for the collapsed Files line: closing it hides the control, and
  // it must not also hide the fact that something is going out attached.
  const attachSummary =
    extraFiles.length === 0
      ? 'nothing attached'
      : extraFiles.length === 1
        ? '1 file attached'
        : `${extraFiles.length} files attached`
  // Only an immediate send can be overridden. A scheduled one is re-checked when
  // it fires, and blocked then — so ticking a box now would promise nothing.
  const blockedByDnc = Boolean(dnc?.suppressed) && when === 'now' && !dncAcknowledged

  return (
    <Modal titleId="send-message-title" title="Send a message" onClose={onClose} showCloseButton>
      {loading ? (
        <div className="text-gray-500 py-4">Loading…</div>
      ) : templates.length === 0 ? (
        <div className="text-gray-500 py-4">
          There are no messages to send. An admin can add one under Admin → Email.
        </div>
      ) : result ? (
        <div className="py-2">
          <div className="alert alert-success mb-4">{result}</div>
          <div className="flex justify-end">
            <button className="btn btn-secondary" onClick={onClose}>Close</button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">

          {firstContact && (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <p>
                <span className="font-medium">Nobody here has spoken with them yet.</span>{' '}
                A first contact usually goes better as a phone call. An e-mail they were not
                expecting, about a deadline, can read like a sales pitch or a scam.
                {firstContact.phone && <> Their number on file: <span className="font-medium">{firstContact.phone}</span>.</>}
              </p>
              <p className="mt-1 text-amber-800">
                {firstContact.isLead
                  ? 'Already called them? Set the lead to Working and this note will not show again.'
                  : 'To keep a record of the call, add them as a lead first.'}
                {' '}You can still send this.
              </p>
            </div>
          )}

          {dnc?.suppressed && (
            <div className="alert alert-warning" role="alert">
              <p className="font-medium">This contact asked not to be contacted.</p>
              <p className="text-sm mt-1">
                Marked as do not contact{dnc.matched_on ? ` — ${dnc.matched_on}` : ''}.
                {dnc.reason ? ` Reason given: ${dnc.reason}` : ''}
              </p>
              {when === 'later' ? (
                <p className="text-sm mt-2">
                  This is checked again when the message is due, and there is nobody there to
                  make an exception — so it will not be sent unless the entry is removed before
                  then.
                </p>
              ) : (
                <div className="flex flex-wrap gap-3 mt-2 items-center">
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={dncAcknowledged}
                      onChange={(e) => setDncAcknowledged(e.target.checked)}
                    />
                    Send anyway
                  </label>
                </div>
              )}
              <div className="flex flex-wrap gap-3 mt-2 items-center">
                {dnc.matches.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className="btn btn-secondary btn-sm"
                    disabled={liftingDnc}
                    onClick={() => handleLiftDnc(m.match_type, m.value)}
                  >
                    {liftingDnc ? 'Removing…' : `Remove do-not-contact for ${m.value}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="label" htmlFor="msg-template">Message *</label>
            <select
              id="msg-template"
              className="input"
              value={templateId ?? ''}
              onChange={(e) => { setTemplateId(Number(e.target.value)); setPreview(null) }}
            >
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
            {selectedTemplate?.description && (
              <p className="text-xs text-gray-500 mt-1">{selectedTemplate.description}</p>
            )}
          </div>

          <div>
            <label className="label" htmlFor="msg-to">Send to *</label>
            <input
              id="msg-to"
              type="email"
              className="input"
              required
              maxLength={255}
              placeholder="recipient@example.com"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
            />
          </div>

          {/* ---- Files ----
              Two shapes, and which one appears is decided by one question: does
              this message name files of its own?

              **It does** — the FCC's own reference copy declares a
              "reference-copy" slot — and the section is rendered here, open, in
              the main run of the form. A required slot is the thing standing
              between the sender and a working Send button, so it can never be
              folded away behind something they have to think to open. An
              optional named slot is still part of what this particular message
              is, so it is shown the same way rather than by a second rule.

              **It does not**, which is now every other message: extra files
              became allowed by default, so this section had started appearing
              on all of them containing nothing but an "Other files" box —
              several lines tall, above the subject and the wording, for
              something almost no send uses. "*Other* files" only means anything
              when there are named files above it to be other *than*; with
              nothing above it, it reads as a question about files that are not
              there. So it becomes one collapsed line down with the rest of the
              optional controls, labelled for what it actually is, and its
              summary counts what is attached — collapsing it can hide the
              control, never a file that is going to be sent. */}
          {slots.length > 0 && (
            <fieldset className="border border-gray-200 rounded p-3">
              <legend className="text-sm font-medium text-gray-700 px-1">Files</legend>

              {slots.map((slot) => {
                const chosen = slotFiles[slot.label]
                return (
                  <div key={slot.label} className="mb-3 last:mb-0">
                    <label className="label" htmlFor={`slot-${slot.label}`}>
                      {slot.label}
                      {slot.required && <span className="text-red-600"> *</span>}
                      {slot.retention_policy === 'days' && slot.retention_days && (
                        <span className="text-gray-400 font-normal">
                          {' '}(kept {slot.retention_days} days)
                        </span>
                      )}
                    </label>
                    {chosen ? (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="truncate">{chosen.original_filename}</span>
                        <button
                          type="button"
                          className="tap text-sm text-primary-600 hover:underline"
                          onClick={() => {
                            setSlotFiles((prev) => {
                              const next = { ...prev }
                              delete next[slot.label]
                              return next
                            })
                            setPreview(null)
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <input
                        id={`slot-${slot.label}`}
                        type="file"
                        className="input text-sm"
                        disabled={uploading !== null}
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) handleUpload(file, slot.label)
                          e.target.value = ''
                        }}
                      />
                    )}
                  </div>
                )
              })}

              {/* Here the label *is* right: there are named files above it. */}
              {allowsExtras && (
                <div className="mt-2">
                  <ExtraFiles
                    label="Other files"
                    optional
                    files={extraFiles}
                    disabled={uploading !== null}
                    onPick={handleAddExtra}
                    onRemove={handleRemoveExtra}
                  />
                </div>
              )}

              {uploading !== null && (
                <p className="mt-2 text-sm text-gray-500">Uploading&hellip;</p>
              )}
              {uploadError && (
                <p className="mt-2 text-sm text-red-600">{uploadError}</p>
              )}
              {unfilledRequired.length > 0 && (
                // Said before they press Send, not after. The server refuses this
                // too — a message that promises a file and arrives without one
                // tells the recipient nothing is wrong.
                <p className="mt-2 text-sm text-amber-700">
                  Still needed: {unfilledRequired.join(', ')}
                </p>
              )}
            </fieldset>
          )}

          {/* ---- Signature, subject, wording ---- */}
          <Disclosure
            id="msg-options"
            open={optionsOpen}
            onToggle={() => setOptionsOpen((v) => !v)}
            label="Signature, subject and wording"
            summary={optionsSummary}
          >
            <div>
              <label className="label" htmlFor="msg-signature">
                Signed by <span className="text-gray-400 font-normal">(optional)</span>
              </label>
              <select
                id="msg-signature"
                className="input"
                value={signatureCardId ?? ''}
                onChange={(e) => {
                  setSignatureCardId(e.target.value ? Number(e.target.value) : null)
                  setPreview(null)
                }}
              >
                <option value="">No signature</option>
                {personalCards.length > 0 && (
                  <optgroup label="Yours">
                    {personalCards.map((c) => (
                      <option key={c.id} value={c.id}>{c.sender_name} — {c.name}</option>
                    ))}
                  </optgroup>
                )}
                {companyCards.length > 0 && (
                  <optgroup label="Company">
                    {companyCards.map((c) => (
                      <option key={c.id} value={c.id}>{c.sender_name} — {c.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>

            <div>
              <FieldHint
                htmlFor="msg-subject"
                label="Subject"
                hintLabel="What the subject box does"
              >
                {senderWritesSubject
                  ? 'This message has no subject of its own, so what you type here is the subject the recipient sees.'
                  : 'Leave this blank to use the message\u2019s own subject, shown greyed out below. Anything you type here is sent instead.'}
              </FieldHint>
              <input
                id="msg-subject"
                type="text"
                className="input"
                aria-describedby="msg-subject-hint"
                maxLength={255}
                // A subject built entirely from values reads as
                // "{{ message_subject }}" here, which is noise; only a template
                // with wording of its own is worth previewing.
                placeholder={
                  senderWritesSubject || !selectedTemplate
                    ? 'Enter a subject'
                    : selectedTemplate.subject_template
                }
                value={subject}
                onChange={(e) => { setSubject(e.target.value); setPreview(null) }}
              />
            </div>

            <div>
              <FieldHint
                htmlFor="msg-body"
                label="Message text"
                hintLabel="What the message text box does"
              >
                {senderWritesSubject
                  ? 'This message carries no wording of its own \u2014 what you type here is the message.'
                  : 'Leave this blank to send the message as it is written. Anything you type here replaces its wording.'}
              </FieldHint>
              <textarea
                id="msg-body"
                className="input min-h-[80px]"
                aria-describedby="msg-body-hint"
                maxLength={20000}
                placeholder={
                  senderWritesSubject
                    ? 'Type your message\u2026'
                    : 'Leave blank to send the message as written.'
                }
                value={body}
                onChange={(e) => { setBody(e.target.value); setPreview(null) }}
              />
            </div>
          </Disclosure>

          {/* The other half of the Files rule above: no named slots, so the box
              for extras sits here, closed, with the rest of the optional
              controls. */}
          {slots.length === 0 && allowsExtras && (
            <Disclosure
              id="msg-attach"
              open={attachOpen}
              onToggle={() => setAttachOpen((v) => !v)}
              label="Attach a file"
              summary={attachSummary}
            >
              <ExtraFiles
                label="Add a file"
                files={extraFiles}
                disabled={uploading !== null}
                onPick={handleAddExtra}
                onRemove={handleRemoveExtra}
              />
              {uploading !== null && (
                <p className="text-sm text-gray-500">Uploading&hellip;</p>
              )}
              {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
            </Disclosure>
          )}

          {/* ---- When ----
              Sending now is the common case and needs no controls at all, so the
              date, time and repeat live behind this. Opening it *is* choosing to
              schedule, and closing it goes back to sending now — the Send button
              spells out which one is about to happen. */}
          <Disclosure
            id="msg-schedule"
            open={when === 'later'}
            onToggle={() => setWhen(when === 'later' ? 'now' : 'later')}
            label="Schedule for later"
            summary="sends straight away"
          >
            <div>
              <FieldHint
                htmlFor="msg-when"
                label="Date and time *"
                hintLabel="How the send time is read"
              >
                A repeating message stays at this time of day when the clocks change.
              </FieldHint>
              <input
                id="msg-when"
                type="datetime-local"
                className="input"
                aria-describedby="msg-when-hint"
                required
                value={sendAtLocal}
                onChange={(e) => setSendAtLocal(e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">Your time zone ({browserTimezone()}).</p>
            </div>

            {/* Repeat only appears once there is something to repeat. */}
            <div>
              <label className="label" htmlFor="msg-repeat">Repeat</label>
              <div className="flex flex-wrap gap-2 items-center">
                <select
                  id="msg-repeat"
                  className="input max-w-[12rem]"
                  value={recurrence}
                  onChange={(e) => setRecurrence(e.target.value as MessageRecurrence)}
                >
                  {RECURRENCES.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
                {recurrence !== 'none' && (
                  <label className="flex items-center gap-2 text-sm">
                    every
                    <input
                      type="number"
                      className="input w-20"
                      min={1}
                      max={99}
                      value={interval}
                      onChange={(e) => setInterval(Math.max(1, Number(e.target.value) || 1))}
                    />
                  </label>
                )}
              </div>
            </div>

            {recurrence !== 'none' && (
              <div>
                <label className="label" htmlFor="msg-until">
                  Stop repeating after <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  id="msg-until"
                  type="datetime-local"
                  className="input"
                  value={until}
                  onChange={(e) => setUntil(e.target.value)}
                />
              </div>
            )}
          </Disclosure>

          {preview && (
            <div className="border border-gray-200 rounded">
              <div className="px-3 py-2 border-b bg-gray-50 text-sm">
                <span className="text-gray-500">Subject:</span>{' '}
                <span className="font-medium text-gray-900">{preview.subject}</span>
              </div>
              <iframe
                title="Message preview"
                className="w-full h-64 bg-white"
                // Sandboxed, but WITH allow-same-origin, matching the admin
                // preview. An opaque-origin iframe cannot satisfy the page's
                // `img-src 'self'` CSP, so the company logo — served from
                // /api/v1/branding/logo — silently fails to load and the header
                // renders as bare text, which is the bug this preview was
                // supposed to stop hiding.
                //
                // This is safe only because `allow-scripts` is absent: the
                // dangerous combination is the two together, which re-enables the
                // frame to remove its own sandbox. Without scripts nothing in a
                // template body can execute, which is why the admin preview
                // already does exactly this.
                sandbox="allow-same-origin"
                srcDoc={preview.html}
              />
            </div>
          )}

          {error && <div className="alert alert-error">{error}</div>}

          <div className="flex flex-wrap justify-end gap-3">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={previewing || !templateId}
              onClick={handlePreview}
            >
              {previewing ? 'Rendering…' : 'Preview'}
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy || blockedByDnc || uploading !== null || unfilledRequired.length > 0}>
              {busy
                ? when === 'now' ? 'Sending…' : 'Scheduling…'
                : when === 'now' ? 'Send now' : 'Schedule'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

/**
 * The unlabelled-extras picker: one file chooser plus the list of what has
 * already been added.
 *
 * It takes its label from the caller because the same control answers two
 * different questions depending on what is above it. Next to a message's own
 * named slots it is "Other files", which is only meaningful *because* there are
 * named ones to be other than. On its own, inside the collapsed section, it is
 * "Add a file" — the section heading already said these are files, and calling
 * them "other" there names a distinction the sender cannot see.
 *
 * One at a time is deliberate and unchanged: each pick uploads immediately and
 * appends, so the list below is the truth about what will be sent.
 */
function ExtraFiles({
  label,
  optional = false,
  files,
  disabled,
  onPick,
  onRemove,
}: {
  label: string
  /** Adds the muted "(optional)" suffix, for when it sits beside required slots. */
  optional?: boolean
  files: StoredFileInfo[]
  disabled: boolean
  onPick: (file: File) => void
  onRemove: (fileId: number) => void
}) {
  return (
    <div>
      <label className="label" htmlFor="extra-file">
        {label}
        {optional && (
          <>
            {' '}
            <span className="text-gray-400 font-normal">(optional)</span>
          </>
        )}
      </label>
      <input
        id="extra-file"
        type="file"
        className="input text-sm"
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) onPick(file)
          // Cleared so picking the same file twice in a row still fires.
          e.target.value = ''
        }}
      />
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((file) => (
            <li key={file.id} className="flex items-center gap-2 text-sm">
              <span className="truncate">{file.original_filename}</span>
              <button
                type="button"
                className="tap text-sm text-primary-600 hover:underline"
                onClick={() => onRemove(file.id)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * A collapsible section of the form: a chevron, a heading, and — while closed —
 * a muted summary of what is set inside, so collapsing hides the controls
 * without hiding the state.
 *
 * The panel is rendered only when open (nothing hidden-but-focusable for the
 * dialog's focus trap to land on, and no hidden `required` field to block
 * submission), while the wrapper keeps the id so `aria-controls` always points
 * at a real element.
 */
function Disclosure({
  id,
  open,
  onToggle,
  label,
  summary,
  children,
}: {
  id: string
  open: boolean
  onToggle: () => void
  label: string
  /** Shown next to the heading while closed. Empty to show nothing. */
  summary?: string
  children: React.ReactNode
}) {
  return (
    <div className="border-t border-gray-100 pt-3">
      <button
        type="button"
        className="tap gap-1.5 max-w-full text-sm font-medium text-gray-600 hover:text-gray-900"
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 flex-shrink-0 transition-transform duration-150 ${open ? 'rotate-90' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
        </svg>
        <span className="flex-shrink-0">{label}</span>
        {!open && summary && (
          <span className="ml-1 truncate text-xs font-normal text-gray-500">{summary}</span>
        )}
      </button>
      <div id={id}>
        {open && <div className="mt-3 space-y-3">{children}</div>}
      </div>
    </div>
  )
}
